// src/data/peopleClient.js
//
// Thin fetch client for GET /api/people/profiles/:locId.
// Returns the camelCase profile object on success, or null when the
// location has no row in location_people_profile (404 → null sentinel).
// All other 4xx/5xx and network failures throw typed errors from errors.js.
//
// PEOPLE-S2: read-only. No writes in this slice.

import { NetworkError, errorFromResponse } from "./errors.js";

const BASE = "/api/people";

/**
 * Fetch the people profile for a single location.
 *
 * @param {string} locId  location ID (e.g. "AMER-NYC-03")
 * @returns {Promise<object|null>}  camelCase profile, or null if no profile exists
 */
export async function getPeopleProfile(locId) {
  let res;
  try {
    res = await fetch(`${BASE}/profiles/${encodeURIComponent(locId)}`);
  } catch {
    throw new NetworkError("Network error fetching people profile.");
  }
  if (res.status === 404) return null;
  if (!res.ok) throw await errorFromResponse(res);
  const profile = await res.json();
  // Normalise the camelCase DB fields to the shape the drawer expects.
  // rowToCamel() on the server produces headcountTotal / employeeCount /
  // contingentCount; the drawer reads totalCount / ftCount / cwCount.
  // Suppressed profiles ({ suppressed: true, locationId, headcountTotal })
  // pass through unchanged — the drawer's `peopleProfile &&` guard handles them.
  if (profile && !profile.suppressed) {
    return {
      ...profile,
      totalCount: profile.headcountTotal ?? null,
      ftCount:    profile.employeeCount   ?? null,
      cwCount:    profile.contingentCount ?? null,
    };
  }
  return profile;
}
